import { gql, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useFetchChatList } from '@modules/oracle/hooks/useFetchChatList';
import { OracleSearchActionType } from '@modules/oracle/store/oracleActionType';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { getWisdomRoute } from '@modules/oracle/utils';
import { GetAssistantResponseInput, Query } from 'generated/graphql';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetUser } from 'v2/core/services/user';

const GET_ASSISTANCE_RESPONSE = gql`
  query getAssistantResponse(
    $question: String!
    $sessionID: ID!
    $tenantID: ID!
    $userID: ID!
    $shouldCreateChat: Boolean!
    $chatEditToken: ID
  ) {
    getAssistantResponse(
      request: {
        question: $question
        sessionID: $sessionID
        tenantID: $tenantID
        userID: $userID
        shouldCreateChat: $shouldCreateChat
        chatEditToken: $chatEditToken
      }
    ) {
      responseID
      chat {
        createdAt
        createdBy {
          firstName
          id
          lastName
        }
        editToken
        id
        sessionID
        status
        tenantID
        title
        updatedAt
        viewToken
      }
    }
  }
`;

interface postQuestionCallbackParams {
  question: string;
  shouldCreateChat: boolean;
}

export function usePostQuestion() {
  const navigate = useNavigate();
  const dispatch = useOracleSearchStore(({ dispatch }) => dispatch);
  const sessionID = useOracleSearchStore(({ data }) => data.sessionID);
  const chatEditToken = useOracleSearchStore(
    ({ data }) => data.chat?.editToken,
  );
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { showErrorNotification } = useNotificationContext();
  const [fetchChatList] = useFetchChatList();
  const [getAssistantResponse, resp] = useLazyQuery<
    Query,
    GetAssistantResponseInput
  >(GET_ASSISTANCE_RESPONSE, {
    fetchPolicy: 'network-only',
    onError: () => {
      showErrorNotification({
        contentComponent: 'Error occurred while posting the question.',
        props: {},
      });
    },
  });

  const postQuestion = useCallback(
    async ({ question, shouldCreateChat }: postQuestionCallbackParams) => {
      if (!sessionID) {
        return;
      }

      // ##TODO, remove it
      console.log(
        'New question posted:',
        JSON.stringify({
          userID,
          question,
          sessionID,
        }),
      );

      const resp = await getAssistantResponse({
        variables: {
          question,
          sessionID,
          tenantID,
          userID,
          shouldCreateChat,
          chatEditToken,
        },
      });

      fetchChatList();

      if (shouldCreateChat) {
        const newChat = resp.data?.getAssistantResponse?.chat;

        dispatch({
          type: OracleSearchActionType.SET_DATA,
          payload: {
            chat: newChat,
          },
        });

        if (newChat?.viewToken) {
          navigate(
            getWisdomRoute({
              name: newChat.title,
              token: newChat.viewToken,
            }),
            {
              replace: true,
              state: { skipReset: true },
            },
          );
        }
      }

      return resp;
    },
    [
      dispatch,
      sessionID,
      chatEditToken,
      tenantID,
      userID,
      getAssistantResponse,
      fetchChatList,
      navigate,
    ],
  );

  return [
    postQuestion,
    {
      responseID: resp.data?.getAssistantResponse?.responseID,
      chat: resp.data?.getAssistantResponse?.chat,
      isLoading: resp.loading,
    },
  ] as const;
}
